import { useQueryClient } from '@tanstack/react-query'

import { ERROR_CODES } from 'api'
import Toast from 'components/Toast'
import useAppQuery from 'hooks/use-app-query'
import useAppMutation from 'hooks/use-app-mutation'
import { getApiKeys, createApiKey, updateApiKey, updateApiKeyStatus } from 'services/api-key'
import { GetApiKeysPayload } from 'services/types/api-key'
import { externalApiKey } from 'utils/constants/queryKey'

const useApiKeys = (payload: GetApiKeysPayload) => useAppQuery(externalApiKey.list(payload), () => getApiKeys(payload))

const useMutations = () => {
  const queryClient = useQueryClient()

  const create = useAppMutation([], createApiKey, {
    onSuccess: () => {
      queryClient.invalidateQueries(externalApiKey.lists())
      Toast.toastMessage({ actionKey: 'create', subject: 'api_key_lbl', variant: 'success' })
    },
    onError: (error) => {
      switch (error?.code) {
        case ERROR_CODES.EXTERNAL_API_KEY_CREATE_ERROR:
          Toast.toastMessage({ message: 'add_external_api_key_required_lead_owner', variant: 'error' })
          break
        default:
          Toast.toastMessage({ variant: 'error', message: error?.message })
      }
    },
  })

  const update = useAppMutation([], updateApiKey, {
    onSuccess: () => {
      queryClient.invalidateQueries(externalApiKey.lists())
      Toast.toastMessage({ actionKey: 'update', subject: 'api_key_lbl', variant: 'success' })
    },
    onError: (err) => {
      switch (err?.code) {
        case ERROR_CODES.EXTERNAL_API_KEY_CREATE_ERROR:
          Toast.toastMessage({ message: 'add_external_api_key_required_lead_owner', variant: 'error' })
          break
        default:
          Toast.toastMessage({ variant: 'error', message: err?.message })
      }
    },
  })

  const activate = useAppMutation([], updateApiKeyStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(externalApiKey.lists())
      Toast.toastMessage({
        actionKey: 'reactivate',
        subject: 'api_key_lbl',
        variant: 'success',
      })
    },
    onError: (err) => {
      Toast.toastMessage({ variant: 'error', message: err?.message })
    },
  })

  const deActivate = useAppMutation([], updateApiKeyStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(externalApiKey.lists())
      Toast.toastMessage({
        actionKey: 'deactivate',
        subject: 'api_key_lbl',
        variant: 'success',
      })
    },
    onError: (err) => {
      Toast.toastMessage({ variant: 'error', message: err?.message })
    },
  })

  const invalidateLists = () => {
    queryClient.invalidateQueries(externalApiKey.lists())
  }

  return {
    create,
    update,
    activate,
    deActivate,
    invalidateLists,
  }
}

export { useApiKeys, useMutations }
